import { api } from './api.util';

/**
 * Get user statistics
 */
export const getUserStats = async () => {
  try {
    const response = await api.get('/users/stats');
    return response;
  } catch (error) {
    console.error('Get user stats error:', error);
    throw error;
  }
};

/**
 * Get users with pagination and filters
 */
export const getUsers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    // Add search parameter
    if (params.search) queryParams.append('search', params.search);
    
    // Add filter parameters
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    
    // Add sorting parameters
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get(`/users?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

/**
 * Invite new user
 */
export const inviteUser = async (userData) => {
  try {
    const response = await api.post('/users/invite', userData);
    return response;
  } catch (error) {
    console.error('Invite user error:', error);
    throw error;
  }
};

/**
 * Bulk invite users from CSV data
 */
export const bulkInviteUsers = async (users) => {
  try {
    const response = await api.post('/users/bulk-invite', { users });
    return response;
  } catch (error) {
    console.error('Bulk invite users error:', error);
    throw error;
  }
};

/**
 * Get bulk job status
 */
export const getBulkJobStatus = async (jobId) => {
  try {
    const response = await api.get(`/users/bulk-status/${jobId}`);
    return response;
  } catch (error) {
    console.error('Get bulk job status error:', error);
    throw error;
  }
};

/**
 * Poll bulk job status until completion
 */
export const pollBulkJobStatus = async (jobId, onProgress, options = {}) => {
  const { maxAttempts = 120, interval = 2000 } = options; // 4 minutes max
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        attempts++;
        const response = await getBulkJobStatus(jobId);
        
        if (response.success) {
          const jobData = response.data;
          
          // Call progress callback
          if (onProgress) {
            onProgress(jobData);
          }

          // Check if job is completed
          if (jobData.status === 'completed') {
            resolve(jobData);
            return;
          }

          if (jobData.status === 'failed') {
            reject(new Error(jobData.error || 'Job failed'));
            return;
          }

          // Continue polling if still processing
          if (attempts < maxAttempts && jobData.status === 'processing') {
            setTimeout(poll, interval);
          } else if (attempts >= maxAttempts) {
            reject(new Error('Job polling timeout'));
          }
        } else {
          reject(new Error('Failed to get job status'));
        }
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};

/**
 * Update user status (suspend/activate)
 */
export const updateUserStatus = async (userId, isActive) => {
  try {
    const response = await api.put(`/users/${userId}/status`, { isActive });
    return response;
  } catch (error) {
    console.error('Update user status error:', error);
    throw error;
  }
};

/**
 * Delete user
 */
export const deleteUser = async (userId) => {
  try {
    const response = await api.delete(`/users/${userId}`);
    return response;
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

/**
 * Get single user details
 */
export const getUser = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}`);
    return response;
  } catch (error) {
    console.error('Get user error:', error);
    throw error;
  }
};

/**
 * Assign exams to users (manual assignment)
 */
export const assignExamsToUsers = async (userIds, examIds) => {
  try {
    const response = await api.post('/users/assign-exams', {
      userIds,
      examIds
    });
    return response;
  } catch (error) {
    console.error('Assign exams error:', error);
    throw error;
  }
}; 